import React, { useState, useEffect, useRef } from "react";
import ReportCount from "../reports/ReportCount";
import FieldOptionReport from "../field_options/FieldOptionReport";
import ProductReport from "../products/ProductReport";
import ProductValueReport from "../products/ProductValueReport";
import FieldReport from "../fields/FieldReport";
import FunnelTrackedMetrics from "../reports/FunnelTrackedMetrics";
import ContentToggleReport from "../content_toggles/ContentToggleReport";
import htmlToLabel from "../../utils/htmlToLabel";
import ProductRecommendationReport from "../product_recommendations/ProductRecommendationReport";
import { schedulerTypeLabel } from "../../utils/schedulerTypes";
import readReportCount from "../../utils/readReportCount";

const SUBMISSIONS_TIMEFRAME_OPTIONS = new Set([
  "Last 24 Hours",
  "Last 7 Days",
  "Last 30 Days",
  "Last 90 Days",
  "Last 365 Days",
]);

const ElementReport = (props) => {
  const element = props.element;
  const idPrefix = props.reportIdPrefix || "";
  const nestedEmbedCtaId =
    element.element_type === "embed" ? element.options?.["embed-cta-id"] : null;
  const reportCtaId = nestedEmbedCtaId || props.cta.id;
  const isNestedEmbed = !!nestedEmbedCtaId;
  const embedLabel =
    element.embedded_cta?.name ||
    (nestedEmbedCtaId ? `Campaign ${nestedEmbedCtaId}` : "");
  const field = element.element_type === "survey" ? element.fields?.[0] : null;
  const fields = (element.fields || [])
    .filter((field) => !field.field_id)
    .sort((a, b) => a.position - b.position);
  const html = ["form", "button", "products", "product_hero"].includes(
    element.element_type
  )
    ? htmlToLabel(element.html)
    : null;
  const nestedEmbedPath = nestedEmbedCtaId
    ? `/websites/${props.cta.website_id}/cta/${nestedEmbedCtaId}`
    : null;
  const elementTitleSuffix = field
    ? ` - ${field.slug}`
    : html
    ? ` - ${html}`
    : embedLabel
    ? ` - ${embedLabel}`
    : "";
  const reportActionButtonClass =
    "bg-white inline-flex items-center justify-center gap-3 border-solid border-[1px] py-3 px-5 rounded-md border-slate-200 text-black text-xl hover:bg-blue-100 hover:border-blue-100 hover:text-blue-600";
  const conversionLabel = ["form", "survey", "quiz"].includes(
    element.element_type
  )
    ? "submitted"
    : ["products", "product_hero"].includes(element.element_type)
    ? "people selected products"
    : element.element_type === "embed"
    ? "converted"
    : element.element_type === "scheduler"
    ? "booked"
    : "clicks";
  const reportScope = {
    contactable_id: reportCtaId,
    variant_id: isNestedEmbed ? null : props.step.variant_id,
    step_id: isNestedEmbed ? null : props.step.id,
    element_id: isNestedEmbed ? null : element.id,
  };
  const submissionsPath = () => {
    const params = new URLSearchParams({
      contactable_id: String(reportCtaId),
      variant_id: String(props.step.variant_id),
      step_id: String(props.step.id),
      element_id: String(element.id),
    });

    if (isNestedEmbed) {
      params.delete("variant_id");
      params.delete("step_id");
      params.delete("element_id");
    }

    if (
      props.timeframeType !== "All Time" &&
      props.range_start &&
      props.range_end
    ) {
      params.set("range_start", props.range_start);
      params.set("range_end", props.range_end);
    }

    if (SUBMISSIONS_TIMEFRAME_OPTIONS.has(props.timeframeType)) {
      params.set("timeframe", props.timeframeType);
    }

    return `/websites/${
      props.cta.website_id
    }/events/submissions?${params.toString()}`;
  };

  const calculationConversionRates = (element) => {
    // Quizzes track answers as Selection events; element Complete/"submitted" is
    // often 0. Use step views so question rows show dropoff vs people who saw
    // the step (same baseline as the StepReport line above).
    const denominatorSpan =
      element.element_type === "quiz"
        ? document.getElementById(`${idPrefix}step-${props.step.id}-views`)
        : document.getElementById(
            `${idPrefix}element-${element.id}-conversions`
          );
    const denominator = readReportCount(denominatorSpan);

    $(
      `.element-selection-report[data-element-id="${element.id}"][data-report-id-prefix="${idPrefix}"]`
    ).each((i, elementSelectionReportDiv) => {
      const selectionsSpan = $(elementSelectionReportDiv).find(
        ".events-report-count"
      )[0];
      const conversionRateElement = $(elementSelectionReportDiv).find(
        ".conversion-rate"
      )[0];
      const conversionBarElement = $(elementSelectionReportDiv).children(
        ".element-selection-report-bar"
      )[0];

      const selections = readReportCount(selectionsSpan);
      let conversionRateValue =
        selections > 0 && denominator > 0
          ? (selections / denominator) * 100
          : 0;

      if (conversionRateValue > 100) {
        conversionRateValue = 100.0;
      }

      $(conversionRateElement).text(conversionRateValue.toFixed(2));

      $(conversionBarElement)
        .css("width", `${conversionRateValue}%`)
        .attr("aria-valuenow", conversionRateValue);

      if (element.element_type !== "quiz") {
        $(elementSelectionReportDiv).css("order", selections);
      }
    });
  };

  const statsParagraph = (element) => {
    return (
      <React.Fragment>
        <p className="light">
          <ReportCount
            inline={true}
            id={`${idPrefix}element-${element.id}-conversions`}
            path={`/websites/${props.cta.website_id}/events_reports`}
            payload={{
              report_type: "distinct_count",
              report: {
                website_id: props.cta.website_id,
                event_type: "Complete",
                contactable_type: "Cta",
                contactable_id: reportScope.contactable_id,
                variant_id: reportScope.variant_id,
                step_id: reportScope.step_id,
                element_id: reportScope.element_id,
                range_start: props.range_start,
                range_end: props.range_end,
                field: null,
                value: null,
                broadcast_id: null,
              },
            }}
            callback={() => {
              calculationConversionRates(element);
            }}
          />{" "}
          {conversionLabel}.
          <FunnelTrackedMetrics
            idBase={`${idPrefix}element-${element.id}`}
            path={`/websites/${props.cta.website_id}/events_reports`}
            currency={props.currency}
            reportingGoals={props.reportingGoals}
            gateReports={props.gateReports}
            report={{
              website_id: props.cta.website_id,
              range_start: props.range_start,
              range_end: props.range_end,
              contactable_type: "Cta",
              contactable_id: reportScope.contactable_id,
              variant_id: reportScope.variant_id,
              step_id: reportScope.step_id,
              element_id: reportScope.element_id,
              field: null,
              value: null,
              product_id: null,
              broadcast_id: null,
            }}
          />
        </p>
      </React.Fragment>
    );
  };

  useEffect(() => {
    if (
      ["conditional_row", "tabs", "carousel", "collapse"].includes(
        element.element_type
      )
    ) {
      const elementReport = $(
        `.element-report[data-element-id="${element.id}"][data-report-id-prefix="${idPrefix}"]`
      );
      const hasElementReports =
        $(elementReport).find(
          `.element-selection-report[data-has-reports="true"]`
        ).length > 0;

      if (!hasElementReports) {
        $(elementReport).css("display", "none");
      }
    }
  }, []);

  return (
    <React.Fragment>
      <div
        className="row hard-center element-report"
        data-element-id={element.id}
        data-report-id-prefix={idPrefix}
      >
        <div className="col-xs-12 no-padding-sides">
          <div className="row hard-center" style={{ marginBottom: "8px" }}>
            <div
              className={
                ["quiz", "form", "survey"].includes(element.element_type) ||
                isNestedEmbed
                  ? "col-xs-8 no-padding-left"
                  : "col-xs-12 no-padding-sides"
              }
            >
              <h4 className="text-2xl bold" style={{ marginBottom: 0 }}>
                <span style={{ textTransform: "capitalize" }}>
                  {element.element_type.replace("_", " ")}{" "}
                  {element.options?.["element-label"]
                    ? `(${element.options["element-label"]})`
                    : ""}
                  {elementTitleSuffix}
                </span>
                {element.element_type === "scheduler" && (
                  <span>
                    {" "}
                    {schedulerTypeLabel(element.options["scheduler_type"])}
                    {element.options["scheduler-url"]
                      ? ` — ${element.options["scheduler-url"]}`
                      : ""}
                  </span>
                )}
              </h4>
            </div>
            {(["quiz", "form", "survey"].includes(element.element_type) ||
              isNestedEmbed) && (
              <div className="col-xs-4 no-padding-right right">
                <div className="inline-flex items-center justify-end flex-wrap gap-3">
                  {nestedEmbedPath && (
                    <a
                      href={nestedEmbedPath}
                      className={reportActionButtonClass}
                      style={{
                        fontWeight: 500,
                        textDecoration: "none",
                      }}
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="18"
                        height="19"
                        viewBox="0 0 18 19"
                        fill="none"
                        aria-hidden="true"
                      >
                        <path
                          d="M2.25 15.125V11.375M7.5 15.125V7.625M12.75 15.125V3.875"
                          stroke="black"
                          strokeWidth="1.3"
                          strokeLinecap="round"
                        />
                      </svg>
                      Campaign
                    </a>
                  )}
                  <a
                    href={submissionsPath()}
                    className={reportActionButtonClass}
                    style={{
                      fontWeight: 500,
                      textDecoration: "none",
                    }}
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="18"
                      height="19"
                      viewBox="0 0 18 19"
                      fill="none"
                      aria-hidden="true"
                    >
                      <path
                        d="M3.375 3.5C3.375 2.87868 3.87868 2.375 4.5 2.375H10.1893C10.4882 2.375 10.7749 2.49377 10.9862 2.70503L14.297 6.01578C14.5082 6.22703 14.627 6.51378 14.627 6.81075V15.5C14.627 16.1213 14.1483 16.625 13.527 16.625H4.5C3.87868 16.625 3.375 16.1213 3.375 15.5V3.5Z"
                        stroke="black"
                        strokeWidth="1.3"
                        strokeLinejoin="round"
                      />
                      <path
                        d="M6.75 10.25H11.25M6.75 13.25H9.75"
                        stroke="black"
                        strokeWidth="1.3"
                        strokeLinecap="round"
                      />
                    </svg>
                    Submissions
                  </a>
                </div>
              </div>
            )}
          </div>
          {props.hide_stats !== true && element.element_type !== "quiz" && (
            <React.Fragment>{statsParagraph(element)}</React.Fragment>
          )}
        </div>
        {["quiz", "form", "survey"].includes(element.element_type) && (
          <div
            className="col-xs-12 no-padding-sides"
            style={{
              width: "100%",
            }}
          >
            {fields
              // Match builder order: show every top-level question (text/number/etc.
              // included). Only hidden fields are omitted. Selection types still
              // expand answer breakdowns via FieldReport. Number labels use
              // sorted index (1..N), same as ElementSettingsQuiz — not raw
              // field.position, which can have gaps after deletes/reorders.
              .filter((field) => field.field_type !== "hidden")
              .map((field, index) => {
                const questionNumber = index + 1;
                return (
                  <React.Fragment key={`field-${field.id}`}>
                    <FieldReport
                      field={field}
                      questionNumber={questionNumber}
                      calculationConversionRates={() => {
                        calculationConversionRates(element);
                      }}
                      {...props}
                    />

                    {element.element_type == "quiz" && !field.field_id && (
                      <div
                        className="row hard-center"
                        style={{ paddingLeft: "30px" }}
                      >
                        {element.fields
                          .filter(
                            (child_field) =>
                              child_field.field_id == field.id &&
                              child_field.field_type !== "hidden"
                          )
                          .sort((a, b) => a.position - b.position)
                          .map((child_field, childIndex) => {
                            return (
                              <React.Fragment key={`field-${child_field.id}`}>
                                <FieldReport
                                  field={child_field}
                                  questionNumber={`${questionNumber}.${
                                    childIndex + 1
                                  }`}
                                  calculationConversionRates={() => {
                                    calculationConversionRates(element);
                                  }}
                                  {...props}
                                />
                              </React.Fragment>
                            );
                          })}
                      </div>
                    )}
                  </React.Fragment>
                );
              })}
          </div>
        )}
        {["products", "product_hero"].includes(element.element_type) && (
          <React.Fragment>
            {(!element.options["products-source"] ||
              element.options["products-source"].toLowerCase() ==
                "convertflow") && (
              <div
                className="col-xs-12 no-padding-sides"
                style={{
                  display: "flex",
                  width: "100%",
                  flexBasis: "100%",
                  flexWrap: "wrap",
                  flexDirection: "column-reverse",
                }}
              >
                {element.products.map((product) => {
                  return (
                    <React.Fragment key={`product-${product.id}`}>
                      <ProductReport
                        key={`product-report-${product.id}`}
                        product={product}
                        label={
                          product.title
                            ? product.title
                            : `Product ${product.position || product.id}`
                        }
                        calculationConversionRates={() => {
                          calculationConversionRates(element);
                        }}
                        {...props}
                      />
                    </React.Fragment>
                  );
                })}
              </div>
            )}

            {(!element.options["products-source"] ||
              element.options["products-source"].toLowerCase() ==
                "convertflow") && (
              <div className="col-xs-12 no-padding-sides w-full">
                <h4 className="text-2xl bold">Product Recommendations</h4>

                {Object.values(element.product_recommendations).map(
                  (product_recommendation) => {
                    return (
                      <React.Fragment
                        key={`product-recommendation-${product_recommendation.id}`}
                      >
                        <ProductRecommendationReport
                          product_recommendation={product_recommendation}
                          element={element}
                          calculationConversionRates={() => {
                            calculationConversionRates(element);
                          }}
                          {...props}
                        />
                      </React.Fragment>
                    );
                  }
                )}
              </div>
            )}

            {["Shopify", "ShopifyGraphql"].includes(
              element.options["products-source"]
            ) && (
              <div
                className="col-xs-12 no-padding-sides"
                style={{
                  display: "flex",
                  width: "100%",
                  flexBasis: "100%",
                  flexWrap: "wrap",
                  flexDirection: "column-reverse",
                }}
              >
                {props.externalProductValues
                  .filter(([_value, elementId]) => elementId == element.id)
                  .map(([value]) => (
                    <ProductValueReport
                      key={`product-value-report-${value}`}
                      label={value}
                      value={value}
                      calculationConversionRates={() => {
                        calculationConversionRates(element);
                      }}
                      {...props}
                    />
                  ))}
              </div>
            )}
          </React.Fragment>
        )}
        {["collapse", "tabs", "carousel", "conditional_row"].includes(
          element.element_type
        ) && (
          <div
            className="col-xs-12 no-padding-sides"
            style={{
              width: "100%",
            }}
          >
            {Object.values(element.content_toggle_items).map((toggle_item) => {
              return (
                <React.Fragment key={`toggle-item-${toggle_item.id}`}>
                  <ContentToggleReport
                    toggle_item={toggle_item}
                    element={element}
                    calculationConversionRates={() => {
                      calculationConversionRates(element);
                    }}
                    {...props}
                  />
                </React.Fragment>
              );
            })}
          </div>
        )}
        {props.hide_stats !== true && element.element_type == "quiz" && (
          <div className="col-xs-12 no-padding-sides">
            {statsParagraph(element)}
          </div>
        )}
      </div>
    </React.Fragment>
  );
};

export default ElementReport;
